"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MultiReviewerConsensusService = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const value_comparison_1 = require("../Comparison/value-comparison");
const comparison_spec_1 = require("./comparison-spec");
const consensus_types_1 = require("./consensus.types");
class MultiReviewerConsensusService {
    static { this.serviceName = MultiReviewerConsensusService.name; }
    constructor(logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
        this.logger = logger;
    }
    analyze(submissions, spec) {
        if (submissions.length === 0) {
            this.logger.error('Analyze_NoReviewers', { service: MultiReviewerConsensusService.serviceName });
            throw new Error('Consensus requires at least one reviewer submission, but received none.');
        }
        const reviewerIds = submissions.map(([reviewerId]) => reviewerId);
        if (new Set(reviewerIds).size !== reviewerIds.length) {
            this.logger.error('Analyze_DuplicateReviewerIds', {
                service: MultiReviewerConsensusService.serviceName,
                reviewerIds,
            });
            throw new Error('Consensus requires a unique reviewer id per submission, but received duplicate ids — ' +
                'one reviewer would otherwise be counted as several, inflating votes and N.');
        }
        const totalReviewers = submissions.length;
        const alignment = spec.alignment;
        let anchorTallies;
        if (alignment instanceof comparison_spec_1.SetAlignment) {
            anchorTallies = this.analyzeSet(submissions, spec, alignment);
        }
        else if (alignment instanceof comparison_spec_1.CellAlignment) {
            anchorTallies = this.analyzeCell(submissions, spec, alignment);
        }
        else {
            this.logger.error('Analyze_UnknownAlignmentMode', {
                service: MultiReviewerConsensusService.serviceName,
                mode: alignment.mode,
            });
            throw new Error(`Unknown alignment mode: ${alignment.mode}`);
        }
        return { totalReviewers, anchorTallies };
    }
    score(report, policy, membershipDenominators) {
        const membershipByRow = new Map();
        for (const tally of report.anchorTallies) {
            if (tally.anchor.kind === consensus_types_1.AnchorKind.Row) {
                membershipByRow.set(tally.anchor.rowKey, tally.candidates[0]?.voteCount ?? 0);
            }
        }
        return report.anchorTallies.map((tally) => {
            const denominator = tally.anchor.kind === consensus_types_1.AnchorKind.Field ?
                (membershipByRow.get(tally.anchor.rowKey) ?? report.totalReviewers)
                : (membershipDenominators?.get(tally.anchor.rowKey) ?? report.totalReviewers);
            return policy.label(tally, denominator);
        });
    }
    scopedMembershipDenominators(report, spec, holdings) {
        const scopeColumns = spec.membershipScopeColumns;
        const denominators = new Map();
        if (scopeColumns === undefined || scopeColumns.length === 0) {
            return denominators;
        }
        const holdingSets = [...holdings.values()];
        for (const tally of report.anchorTallies) {
            if (tally.anchor.kind !== consensus_types_1.AnchorKind.Row) {
                continue;
            }
            const membership = tally.candidates[0];
            const row = membership?.value;
            if (membership === undefined || row === null || typeof row !== 'object') {
                continue;
            }
            const scopeValues = scopeColumns.map((column) => spec.normalize(column, row[column], row));
            const holders = holdingSets.filter((held) => scopeValues.every((value) => held.has(value))).length;
            if (holders < membership.voteCount) {
                this.logger.warn('ScopedMembershipDenominators_HolderInvariantViolated', {
                    service: MultiReviewerConsensusService.serviceName,
                    rowKey: tally.anchor.rowKey,
                    holders,
                    voteCount: membership.voteCount,
                });
                continue;
            }
            denominators.set(tally.anchor.rowKey, holders);
        }
        return denominators;
    }
    triage(scored, policy) {
        return scored.map((tally) => this.routeTally(tally, policy));
    }
    acceptUnfilledFields(triaged) {
        return triaged.map((tally) => {
            if (tally.anchor.kind !== consensus_types_1.AnchorKind.Field || tally.triagePath === consensus_types_1.TriagePath.AutoAccept) {
                return tally;
            }
            const allBlank = tally.candidates.length > 0 && tally.candidates.every((candidate) => candidate.normalizedValue === '');
            if (!allBlank) {
                return tally;
            }
            return {
                anchor: tally.anchor,
                triagePath: consensus_types_1.TriagePath.AutoAccept,
                candidates: tally.candidates,
                resolved: tally.candidates[0].value,
            };
        });
    }
    autoResolve(triaged, autoResolveColumns) {
        if (autoResolveColumns.size === 0) {
            return triaged;
        }
        return triaged.map((tally) => {
            if (tally.anchor.kind !== consensus_types_1.AnchorKind.Field) {
                return tally;
            }
            const policy = autoResolveColumns.get(tally.anchor.column);
            if (policy === undefined) {
                return tally;
            }
            return {
                anchor: tally.anchor,
                triagePath: consensus_types_1.TriagePath.AutoAccept,
                candidates: tally.candidates,
                resolved: policy.pick(tally.candidates),
            };
        });
    }
    assemble(triaged, spec, assembler, totalReviewers) {
        return assembler.assemble(triaged, spec, totalReviewers);
    }
    run(submissions, config) {
        const report = this.analyze(submissions, config.spec);
        const membershipDenominators = config.membershipHoldings === undefined ?
            undefined
            : this.scopedMembershipDenominators(report, config.spec, config.membershipHoldings);
        const scored = this.score(report, config.confidencePolicy, membershipDenominators);
        const triaged = this.acceptUnfilledFields(this.triage(scored, config.triagePolicy));
        const resolved = this.autoResolve(triaged, this.stringKeyedAutoResolve(config.spec.autoResolveColumns));
        return this.assemble(resolved, config.spec, config.assembler, report.totalReviewers);
    }
    stringKeyedAutoResolve(columns) {
        const byName = new Map();
        for (const [column, policy] of columns) {
            byName.set(String(column), policy);
        }
        return byName;
    }
    routeTally(tally, policy) {
        const path = policy.triagePathFor(tally);
        if (path !== consensus_types_1.TriagePath.AutoAccept) {
            return { anchor: tally.anchor, triagePath: path, candidates: tally.candidates };
        }
        const clearers = tally.candidates.filter((candidate) => policy.meetsAutoAcceptBar(candidate));
        if (clearers.length === 1) {
            return {
                anchor: tally.anchor,
                triagePath: consensus_types_1.TriagePath.AutoAccept,
                candidates: tally.candidates,
                resolved: clearers[0].value,
            };
        }
        return { anchor: tally.anchor, triagePath: consensus_types_1.TriagePath.ExpertReview, candidates: tally.candidates };
    }
    analyzeCell(submissions, spec, alignment) {
        const keyColumns = alignment.keyColumns.map(String);
        const valueColumns = alignment.valueColumns.map(String);
        const normalize = (column, value, row) => spec.normalize(column, value, row);
        const perReviewer = submissions.map(([reviewerId, data]) => ({
            reviewerId,
            index: (0, value_comparison_1.indexByKey)(spec.toRows(data), keyColumns, normalize, (rowKey, kept, dropped) => this.logger.warn('AnalyzeCell_DuplicateRowKey', {
                service: MultiReviewerConsensusService.serviceName,
                reviewerId,
                rowKey,
                divergentValues: valueColumns
                    .filter((column) => kept[column] !== dropped[column])
                    .map((column) => ({ column, kept: kept[column], dropped: dropped[column] })),
            })),
        }));
        const orderedKeys = this.collectOrderedKeys(perReviewer);
        const anchorTallies = [];
        for (const rowKey of orderedKeys) {
            const reviewersWithRow = perReviewer.filter((entry) => entry.index.has(rowKey));
            const rowsForKey = reviewersWithRow
                .map((entry) => entry.index.get(rowKey))
                .filter((row) => row !== undefined);
            const representativeRow = this.pickRepresentative(rowsForKey);
            anchorTallies.push({
                anchor: { kind: consensus_types_1.AnchorKind.Row, rowKey },
                candidates: [
                    {
                        value: representativeRow,
                        normalizedValue: rowKey,
                        voteCount: reviewersWithRow.length,
                        proposedBy: reviewersWithRow.map((entry) => entry.reviewerId),
                    },
                ],
            });
            for (const column of valueColumns) {
                anchorTallies.push({
                    anchor: { kind: consensus_types_1.AnchorKind.Field, rowKey, column },
                    candidates: this.tallyFieldCandidates(reviewersWithRow, rowKey, column, normalize),
                });
            }
        }
        return anchorTallies;
    }
    analyzeSet(submissions, spec, alignment) {
        const normalize = (column, value, row) => spec.normalize(column, value, row);
        const perReviewerRows = submissions.map(([reviewerId, data]) => [reviewerId, spec.toRows(data)]);
        const columns = this.collectColumnUnion(perReviewerRows.flatMap(([, rows]) => rows));
        const ordered = [];
        const byIdentity = new Map();
        for (const [reviewerId, rows] of perReviewerRows) {
            const seenThisReviewer = new Set();
            for (const row of rows) {
                const identity = alignment.identityOf(row, normalize, columns);
                if (seenThisReviewer.has(identity)) {
                    this.logger.warn('AnalyzeSet_DuplicateIdentity', {
                        service: MultiReviewerConsensusService.serviceName,
                        reviewerId,
                        identity,
                    });
                    continue;
                }
                seenThisReviewer.add(identity);
                let member = byIdentity.get(identity);
                if (!member) {
                    member = { rows: [], proposedBy: [] };
                    byIdentity.set(identity, member);
                    ordered.push(identity);
                }
                member.rows.push(row);
                member.proposedBy.push(reviewerId);
            }
        }
        return ordered.map((identity) => {
            const member = byIdentity.get(identity);
            return {
                anchor: { kind: consensus_types_1.AnchorKind.Row, rowKey: identity },
                candidates: [
                    {
                        value: this.pickRepresentative(member.rows),
                        normalizedValue: identity,
                        voteCount: member.proposedBy.length,
                        proposedBy: member.proposedBy,
                    },
                ],
            };
        });
    }
    collectOrderedKeys(perReviewer) {
        const ordered = [];
        const seen = new Set();
        for (const { index } of perReviewer) {
            for (const key of index.keys()) {
                if (!seen.has(key)) {
                    seen.add(key);
                    ordered.push(key);
                }
            }
        }
        return ordered;
    }
    collectColumnUnion(rows) {
        const ordered = [];
        const seen = new Set();
        for (const row of rows) {
            for (const key of Object.keys(row)) {
                if (!seen.has(key)) {
                    seen.add(key);
                    ordered.push(key);
                }
            }
        }
        return ordered;
    }
    pickRepresentative(rows) {
        if (rows.length === 0) {
            return undefined;
        }
        const grouped = new Map();
        for (const row of rows) {
            const serialized = this.stableSerialize(row);
            const entry = grouped.get(serialized);
            if (entry) {
                entry.count += 1;
            }
            else {
                grouped.set(serialized, { row, count: 1 });
            }
        }
        return [...grouped.entries()].sort(([keyA, a], [keyB, b]) => b.count - a.count || keyA.localeCompare(keyB))[0][1]
            .row;
    }
    stableSerialize(row) {
        return JSON.stringify(Object.keys(row)
            .sort()
            .map((key) => [key, row[key]]));
    }
    tallyFieldCandidates(reviewersWithRow, rowKey, column, normalize) {
        const groups = new Map();
        for (const { reviewerId, index } of reviewersWithRow) {
            const row = index.get(rowKey);
            const original = row ? row[column] : undefined;
            const normalized = normalize(column, original, row);
            let group = groups.get(normalized);
            if (!group) {
                group = { value: original, normalizedValue: normalized, voteCount: 0, proposedBy: [] };
                groups.set(normalized, group);
            }
            group.voteCount += 1;
            group.proposedBy.push(reviewerId);
        }
        return [...groups.values()];
    }
}
exports.MultiReviewerConsensusService = MultiReviewerConsensusService;
//# sourceMappingURL=multi-reviewer-consensus.service.js.map